(() => {
  'use strict';

  if (customElements.get('block-sections-carousel')) return;

  class BlockSectionsCarousel extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.cancelSlideTransition();
      this.carouselController?.destroy();
      this.carouselController = null;

      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.viewport = this.querySelector('[data-block-sections-ref="viewport"]');
      this.track = this.querySelector('[data-block-sections-ref="track"]');
      this.status = this.querySelector('[data-block-sections-ref="status"]');
      this.count = this.querySelector('[data-block-sections-ref="count"]');
      this.motionPreference = (window.DieuxFrame?.motionQuery ?? window.matchMedia('(prefers-reduced-motion: reduce)'));
      this.announceOnSlideChange = true;

      this.refreshSlides();
      if (this.slides.length < 2 || !this.viewport || !this.track) {
        this.dataset.swiperState = 'static';
        return;
      }

      this.addEventListener('click', this.handleClick, {
        capture: true,
        signal,
      });
      this.viewport.addEventListener('keydown', this.handleViewportKeydown, { signal });
      window.addEventListener('dieux:swiper:ready', this.mountCarousel, { signal });
      this.motionPreference.addEventListener('change', this.handleMotionPreferenceChange, {
        signal,
      });

      this.mountCarousel();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.cancelSlideTransition();
      this.carouselController?.destroy();
      this.carouselController = null;
      this.restoreStaticSlideState();
    }

    refreshSlides() {
      this.slides = Array.from(this.querySelectorAll('[data-block-sections-slide]'));
      const hasMultipleSlides = this.slides.length > 1;
      const template = this.dataset.slideStatusTemplate || 'Slide __index__ of __length__';

      this.slides.forEach((slide, index) => {
        const labelledby = slide.dataset.blockSectionsSlideLabelledby;
        if (!hasMultipleSlides) {
          slide.removeAttribute('aria-roledescription');
          slide.removeAttribute('aria-label');
          if (labelledby) {
            slide.setAttribute('role', 'group');
            slide.setAttribute('aria-labelledby', labelledby);
          } else {
            slide.removeAttribute('role');
          }
          return;
        }

        const status = template
          .replace('__index__', String(index + 1))
          .replace('__length__', String(this.slides.length));
        const label = slide.dataset.blockSectionsSlideLabel?.trim();
        slide.setAttribute('role', 'group');
        slide.removeAttribute('aria-labelledby');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', label ? `${label}, ${status}` : status);
      });
    }

    mountCarousel = () => {
      this.carouselController?.destroy();
      this.carouselController = null;

      if (
        this.slides.length < 2
        || !(this.viewport instanceof HTMLElement)
        || !(this.track instanceof HTMLElement)
        || !window.DieuxSwiper
      ) {
        return;
      }

      const carousel = this;
      this.lastAnnouncedIndex = this.initialSlide || 0;
      this.carouselController = window.DieuxSwiper.mount({
        owner: this,
        viewport: this.viewport,
        track: this.track,
        stateTarget: this,
        previousButton: this.querySelector(
          '.block-sections__controls [data-swiper-button="previous"]'
        ),
        nextButton: this.querySelector('.block-sections__controls [data-swiper-button="next"]'),
        mousewheel: true,
        options: {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: true,
          initialSlide: this.initialSlide || 0,
          a11y: {
            enabled: false,
          },
          on: {
            slideChange(swiper) {
              const activeIndex = swiper.realIndex ?? swiper.activeIndex;
              const outgoingSlide = carousel.slides.find(
                (slide) => slide.dataset.current === 'true'
              );
              if (
                outgoingSlide?.contains(document.activeElement)
                && carousel.viewport instanceof HTMLElement
              ) {
                const focusLanding = window.DieuxAnchorScroll?.focusLanding;
                if (typeof focusLanding === 'function') focusLanding(carousel.viewport);
                else carousel.viewport.focus({ preventScroll: true, focusVisible: false });
              }
              carousel.syncSlideState(activeIndex, true);
              if (
                carousel.announceOnSlideChange
                && activeIndex !== carousel.lastAnnouncedIndex
              ) {
                carousel.announceSlide(activeIndex);
              }
              carousel.lastAnnouncedIndex = activeIndex;
            },
          },
        },
        onStateChange: ({ state, controller }) => {
          if (state === 'enhanced') {
            const activeIndex =
              controller.instance?.realIndex ?? controller.instance?.activeIndex ?? 0;
            this.lastAnnouncedIndex = activeIndex;
            this.syncSlideState(activeIndex, true);
            requestAnimationFrame(() => {
              controller.refresh();
            });
          } else {
            this.restoreStaticSlideState();
          }
        },
      });
    };

    handleClick = (event) => {
      if (!(event.target instanceof Element)) return;
      const button = event.target.closest('[data-swiper-button]');
      if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      if (button.disabled || this.hasAttribute('data-slide-transitioning')) return;

      const delta = button.dataset.swiperButton === 'previous' ? -1 : 1;
      this.navigateBy(delta);
    };

    handleMotionPreferenceChange = () => {
      this.cancelSlideTransition();
    };

    handleViewportKeydown = (event) => {
      if (event.target !== this.viewport || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      const instance = this.carouselController?.instance;
      if (!instance || instance.destroyed) return;

      event.preventDefault();
      if (this.hasAttribute('data-slide-transitioning')) return;
      const delta = event.key === 'ArrowLeft' ? -1 : 1;
      this.navigateBy(delta);
    };

    navigateBy(delta) {
      Promise.resolve(this.carouselController?.promote()).then(() => {
        const instance = this.carouselController?.instance;
        if (!instance || instance.destroyed) return;

        const activeIndex = instance.realIndex ?? instance.activeIndex;
        const nextIndex = (activeIndex + delta + this.slides.length) % this.slides.length;
        this.transitionTo(nextIndex);
      });
    }

    async transitionTo(nextIndex, { announce = true } = {}) {
      const instance = this.carouselController?.instance;
      if (
        !instance
        || instance.destroyed
        || nextIndex === (instance.realIndex ?? instance.activeIndex)
        || !(this.viewport instanceof HTMLElement)
      ) {
        return;
      }

      if (
        this.motionPreference.matches
        || typeof this.viewport.animate !== 'function'
      ) {
        this.commitSlide(nextIndex, announce);
        return;
      }

      this.cancelSlideTransition();
      const transitionId = this.slideTransitionId;
      this.dataset.slideTransitioning = 'true';
      const outgoing = this.viewport.animate(
        [
          { opacity: 1, transform: 'translateY(0) scale(1)' },
          { opacity: 0, transform: 'translateY(-6px) scale(0.996)' },
        ],
        {
          duration: 300,
          easing: 'cubic-bezier(.4, 0, 1, 1)',
          fill: 'both',
        }
      );
      this.slideAnimations.add(outgoing);
      await outgoing.finished.catch(() => {});
      if (transitionId !== this.slideTransitionId || !this.isConnected) return;

      this.commitSlide(nextIndex, announce);

      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (transitionId !== this.slideTransitionId || !this.isConnected) return;

      const incoming = this.viewport.animate(
        [
          { opacity: 0, transform: 'translateY(8px) scale(0.994)' },
          { opacity: 1, transform: 'translateY(0) scale(1)' },
        ],
        {
          duration: 300,
          easing: 'cubic-bezier(.22, 1, .36, 1)',
          fill: 'both',
        }
      );
      outgoing.cancel();
      this.slideAnimations.delete(outgoing);
      this.slideAnimations.add(incoming);
      await incoming.finished.catch(() => {});
      if (transitionId !== this.slideTransitionId) return;

      incoming.cancel();
      this.slideAnimations.delete(incoming);
      delete this.dataset.slideTransitioning;
    }

    cancelSlideTransition() {
      this.slideTransitionId = (this.slideTransitionId || 0) + 1;
      this.slideAnimations?.forEach((animation) => animation.cancel());
      this.slideAnimations = new Set();
      delete this.dataset.slideTransitioning;
    }

    commitSlide(index, announce) {
      const instance = this.carouselController?.instance;
      if (!instance || instance.destroyed) return;

      this.announceOnSlideChange = announce;
      instance.slideToLoop(index, 0);
      this.announceOnSlideChange = true;
    }

    syncSlideState(activeIndex, lockInactiveSlides) {
      this.slides.forEach((slide, index) => {
        const isCurrent = index === activeIndex;
        slide.dataset.current = String(isCurrent);
        if (lockInactiveSlides) {
          slide.setAttribute('aria-hidden', String(!isCurrent));
          slide.inert = !isCurrent;
        } else {
          slide.removeAttribute('aria-hidden');
          slide.inert = false;
        }
      });

      if (this.count instanceof HTMLElement) {
        this.count.textContent = `${activeIndex + 1}/${this.slides.length}`;
      }
    }

    restoreStaticSlideState() {
      this.syncSlideState(0, false);
    }

    announceSlide(index) {
      if (this.status instanceof HTMLElement) {
        this.status.textContent = this.slides[index]?.getAttribute('aria-label') || '';
      }
    }

    selectBlock(blockId, sourceElement) {
      this.cancelSlideTransition();
      this.refreshSlides();
      const sourceSlide =
        sourceElement instanceof Element
          ? sourceElement.closest('[data-block-sections-slide]')
          : null;
      const nextIndex = sourceSlide
        ? this.slides.indexOf(sourceSlide)
        : this.slides.findIndex((slide) => slide.dataset.blockSectionsBlockId === blockId);
      if (nextIndex < 0) return;

      Promise.resolve(this.carouselController?.promote()).then(() => {
        const instance = this.carouselController?.instance;
        if (instance && !instance.destroyed) {
          this.commitSlide(nextIndex, false);
        } else if (this.viewport instanceof HTMLElement) {
          this.viewport.scrollTo({
            left: this.slides[nextIndex].offsetLeft,
            behavior: 'auto',
          });
        }
      });
    }

  }

  customElements.define('block-sections-carousel', BlockSectionsCarousel);
})();
